import { Alert, Skeleton, Title } from "@mantine/core";
import {
  BookOpen,
  Film,
  Image,
  LayoutDashboard,
  Music,
  PieChart as PieChartIcon,
  Tags,
  TrendingUp,
  Tv,
  Users,
  Video,
  Zap,
} from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useValidate from "../hooks/useValidate";
import { getDashboard } from "../utils/api";
import useStore from "../utils/store";

const StatCard = ({ title, value, icon: Icon, to, variant = "default", color = "zinc" }) => (
  <Link to={to || "#"} className="block group">
    <div
      className={`rounded-2xl border transition-all duration-300 p-5 h-full shadow-sm hover:shadow-xl hover:-translate-y-0.5
        ${variant === "primary"
          ? `bg-gradient-to-br from-zinc-900/90 to-zinc-800/90 border-blue-500/40 hover:border-blue-400/70`
          : `bg-zinc-900/70 border-zinc-700/60 hover:border-${color}-500/30 group-hover:bg-zinc-800/70`
        }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-zinc-400 text-sm font-medium truncate">{title}</p>
          <p className={`mt-2 truncate ${variant === "primary" ? "text-3xl font-bold text-white" : "text-2xl font-semibold text-zinc-100"}`}>
            {value ?? 0}
          </p>
        </div>
        <div
          className={`shrink-0 size-12 rounded-2xl flex items-center justify-center transition-colors
            ${variant === "primary" 
              ? "bg-blue-500/20 text-blue-400 shadow-inner" 
              : `bg-${color}-500/10 text-${color}-400 group-hover:text-${color}-300`
            }`}
        >
          <Icon size={26} strokeWidth={1.75} />
        </div>
      </div>
    </div>
  </Link>
);

const ChartCard = ({ title, subtitle, icon: Icon, children }) => (
  <div className="rounded-3xl border border-zinc-700/50 bg-zinc-900/70 p-6 shadow-inner">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div className="size-10 rounded-2xl bg-zinc-800/80 flex items-center justify-center text-zinc-300 shadow-inner">
          <Icon size={22} strokeWidth={2} />
        </div>
        <div>
          <h3 className="text-white font-semibold text-lg">{title}</h3>
          {subtitle && <p className="text-zinc-400 text-sm mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
    {children}
  </div>
);

const SectionHeader = ({ title, icon: Icon }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon size={19} className="text-zinc-400" />
    <h2 className="text-zinc-300 font-semibold text-sm uppercase tracking-widest">{title}</h2>
  </div>
);

const Dashboard = () => {
  const [data, setData] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const user = useStore((state) => state.user);

  const getDashboardData = async () => {
    setLoading(true);
    const [success, response] = await getDashboard();
    if (success) setData(response || {});
    setLoading(false);
  };

  useValidate();

  React.useEffect(() => {
    getDashboardData();
  }, []);

  // Format user growth daily data
  const formattedUserGrowthData = data.userGrowthDaily?.map((item) => ({
    date: new Date(item.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    count: item.count,
  })) || [];

  // Content distribution for Pie Chart
  const contentData = [
    { name: "Movies", value: data.movies || 0, color: "#10b981" },
    { name: "Series", value: data.series || 0, color: "#3b82f6" },
    { name: "Documentaries", value: data.documentary || 0, color: "#8b5cf6" },
    { name: "Music Videos", value: data.musicVideo || 0, color: "#ec4899" },
    { name: "Short Films", value: data.shortFilm || 0, color: "#f59e0b" },
  ].filter((item) => item.value > 0);

  const totalContent = contentData.reduce((sum, item) => sum + item.value, 0);

  const isLoading = loading;
  const formatVal = (val) => (typeof val === "number" ? val.toLocaleString() : val || 0);

  return user?.getDashboard ? (
    <div className="dashboard-page w-full max-w-7xl mx-auto">
      {/* Header - more visual */}
      <div className="mb-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-zinc-400 mb-2">
              <LayoutDashboard size={20} />
              <span className="text-sm font-medium tracking-widest">PLATFORM OVERVIEW</span>
            </div>
            <Title order={1} className="text-white text-3xl sm:text-4xl font-bold tracking-tighter">
              Dashboard
            </Title>
            <p className="text-zinc-400 text-base mt-2 max-w-md">
              Real-time insights into your media empire
            </p>
          </div>

          <div className="flex items-center gap-3 bg-zinc-900/80 border border-zinc-700/50 rounded-3xl px-5 py-3 text-sm">
            <div className="flex items-center gap-1.5">
              <div className="size-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-emerald-400 font-medium">LIVE</span>
            </div>
            <Zap size={16} className="text-amber-400" />
            <span className="text-zinc-400">Updated just now</span>
          </div>
        </div>
      </div>

      {/* Key metrics - top row with more visual flair */}
      <div className="grid grid-cols-2 gap-6 mb-12">
        <StatCard
          title="Total Users"
          value={formatVal(data.users ?? 0)}
          icon={Users}
          to="/users"
        />
        <StatCard
          title="Total Content"
          value={formatVal(
            (data.movies || 0) +
              (data.series || 0) +
              (data.documentary || 0) +
              (data.musicVideo || 0) +
              (data.shortFilm || 0)
          )}
          icon={Film}
          to="/movies"
          color="emerald"
        />
      </div>

      {/* Dual Charts - now side-by-side and more graphical */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
        {/* User Growth Chart */}
        <ChartCard title="Daily User Growth" subtitle="New registrations • Last 14 days" icon={Users}>
          {isLoading ? (
            <Skeleton height={340} radius="xl" />
          ) : (
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={formattedUserGrowthData} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#52525b"
                    tick={{ fill: "#a1a1aa", fontSize: 11 }}
                    angle={-35}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    stroke="#52525b"
                    tick={{ fill: "#a1a1aa", fontSize: 11 }}
                    tickFormatter={(v) => v.toLocaleString()}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#18181b",
                      border: "1px solid #3b82f6",
                      borderRadius: "16px",
                      color: "#fff",
                      boxShadow: "0 10px 15px -3px rgb(59 130 246)",
                    }}
                    formatter={(value) => [`${Number(value).toLocaleString()} new users`, "Registrations"]}
                    labelFormatter={(label) => `📅 ${label}`}
                  />
                  <Bar dataKey="count" fill="#10b981" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        {/* New: Content Distribution Pie Chart */}
        <ChartCard title="Content Distribution" subtitle="Library breakdown" icon={PieChartIcon}>
          {isLoading ? (
            <Skeleton height={340} radius="xl" />
          ) : (
            <div className="h-[340px] flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={contentData}
                    cx="50%"
                    cy="50%"
                    innerRadius={85}
                    outerRadius={130}
                    paddingAngle={3}
                    dataKey="value"
                    cornerRadius={6}
                  >
                    {contentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #27272a",
                      borderRadius: "16px",
                      color: "#fff",
                    }}
                    formatter={(value, name) => [`${value.toLocaleString()} items`, name]}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    iconType="circle"
                    wrapperStyle={{ fontSize: "13px", color: "#d1d5db" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>
      </div>

      {/* Content stats - grouped by section */}
      <SectionHeader title="Users" icon={Users} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-12">
        {[
          { title: "Today", value: data.todayUsers, color: "emerald" },
          { title: "Weekly", value: data.weeklyUsers, color: "blue" },
          { title: "Monthly", value: data.monthlyUsers, color: "violet" },
          { title: "Total", value: data.users, color: "zinc" },
        ].map((item) => (
          <StatCard
            key={item.title}
            title={item.title}
            value={formatVal(item.value)}
            icon={Users}
            to="/users"
            color={item.color}
          />
        ))}
      </div>

      <SectionHeader title="Movies & Series" icon={Film} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
        {[
          { title: "Movies", value: data.movies, icon: Film, color: "emerald", to: "/movies" },
          { title: "Series", value: data.series, icon: Tv, color: "blue", to: "/series" },
          { title: "Documentaries", value: data.documentary, icon: Video, color: "violet", to: "/documentaries" },
          { title: "Music Videos", value: data.musicVideo, icon: Music, color: "pink", to: "/music-videos" },
          { title: "Short Films", value: data.shortFilm, icon: Film, color: "amber", to: "/short-films" },
        ].map((item) => (
          <StatCard key={item.title} {...item} value={formatVal(item.value)} />
        ))}
      </div>

      <SectionHeader title="Subscriptions & Other" icon={BookOpen} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
        {[
          { title: "Genres", value: data.geners ?? data.genres, icon: Tags, color: "zinc", to: "/genres" },
          { title: "Banners", value: data.banners, icon: Image, color: "slate", to: "/banners" },
          { title: "Active Subs", value: data.activeSubscriptions, icon: TrendingUp, color: "emerald", to: "/active-subscription" },
          { title: "Non-Active Subs", value: data.nonActiveSubscriptions, icon: BookOpen, color: "rose", to: "/admin/non-active-subscriptions" },
        ].map((item) => (
          <StatCard key={item.title} {...item} value={formatVal(item.value)} />
        ))}
      </div>
    </div>
  ) : (
    <div className="flex justify-center items-center min-h-[50vh]">
      <Alert color="red" title="Access Denied" variant="light" radius="xl">
        You are not authorized to access this page.
      </Alert>
    </div>
  );
};

export default Dashboard;